import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Row, Col, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Card, Checkbox, Divider, Empty, Input, InputNumber, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  createCycleMatiere,
  createCycleSousMatiere,
  deleteCycleSousMatiere,
  getCycle,
  getCycleClasses,
  getCycleMatieres,
  getCycleScoringMode,
  getCycleSousMatieres,
  getEcoleClasses,
  getEcoleMatieres,
  getMatiereCoefficients,
  ICycleClasse,
  ICycleCoefficient,
  ICycleMatiere,
  ICycleSousMatiere,
  linkCycleClasses,
  linkExistingCycleMatiere,
  saveCycleCoefficients,
  unlinkCycleClasse,
  unlinkCycleMatiere,
  updateCycleSousMatiere,
} from './cycle.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { scoringModeLabels } from 'app/shared/model/cycle.model';

const KORI_ORANGE = '#f59b42';
const KORI_ORANGE_LIGHT = '#fff4e8';
const orangeButtonStyle = { backgroundColor: KORI_ORANGE, borderColor: KORI_ORANGE, color: '#fff' };

export const CycleDetail = (props: RouteComponentProps<{ ecoleid: string; id: string }>) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getCycle(props.match.params.id));
  }, [dispatch, props.match.params.id]);

  const cycleEntity = useAppSelector(state => state.cycle.entity);
  const [cycleUsesCoefficients, setCycleUsesCoefficients] = useState(false);
  const [classes, setClasses] = useState<ICycleClasse[]>([]);
  const [ecoleClasses, setEcoleClasses] = useState<ICycleClasse[]>([]);
  const [selectedClasseIds, setSelectedClasseIds] = useState<string[]>([]);
  const [linking, setLinking] = useState(false);
  const [classeToUnlink, setClasseToUnlink] = useState<ICycleClasse | null>(null);
  const [matieres, setMatieres] = useState<ICycleMatiere[]>([]);
  const [ecoleMatieres, setEcoleMatieres] = useState<ICycleMatiere[]>([]);
  const [selectedMatiereId, setSelectedMatiereId] = useState<string | undefined>();
  const [selectedExistingMatiereId, setSelectedExistingMatiereId] = useState<string | undefined>();
  const [newMatiereName, setNewMatiereName] = useState('');
  const [matiereAvecSousMatieres, setMatiereAvecSousMatieres] = useState(false);
  const [existingMatiereAvecSousMatieres, setExistingMatiereAvecSousMatieres] = useState(false);
  const [matiereCreateModalOpen, setMatiereCreateModalOpen] = useState(false);
  const [savingMatiere, setSavingMatiere] = useState(false);
  const [sousMatieres, setSousMatieres] = useState<ICycleSousMatiere[]>([]);
  const [newSousMatiereName, setNewSousMatiereName] = useState('');
  const [editingSousMatiereId, setEditingSousMatiereId] = useState<string | null>(null);
  const [editingSousMatiereName, setEditingSousMatiereName] = useState('');
  const [savingSousMatiere, setSavingSousMatiere] = useState(false);
  const [coefficientsByClasseId, setCoefficientsByClasseId] = useState<Record<string, ICycleCoefficient>>({});
  const [coefficientValues, setCoefficientValues] = useState<Record<string, number | null>>({});
  const [loadingCoefficients, setLoadingCoefficients] = useState(false);
  const [savingCoefficients, setSavingCoefficients] = useState(false);

  useEffect(() => {
    const fetchScoringMode = async () => {
      if (!cycleEntity?.id) {
        setCycleUsesCoefficients(false);
        return;
      }
      const scoringMode = await dispatch(getCycleScoringMode(cycleEntity.id)).unwrap();
      setCycleUsesCoefficients(Boolean(scoringMode.usesCoefficients));
    };
    fetchScoringMode();
  }, [cycleEntity?.id, dispatch]);

  useEffect(() => {
    const fetchClasses = async () => {
      const response = await dispatch(getCycleClasses(props.match.params.id)).unwrap();
      setClasses(response);
    };
    fetchClasses();
  }, [dispatch, props.match.params.id]);

  useEffect(() => {
    const fetchEcoleClasses = async () => {
      if (!cycleEntity?.ecoleId) return;
      const response = await dispatch(getEcoleClasses(cycleEntity.ecoleId)).unwrap();
      setEcoleClasses(response);
    };
    fetchEcoleClasses();
  }, [cycleEntity?.ecoleId, dispatch]);

  const fetchCycleMatieres = useCallback(async () => {
    if (!cycleEntity?.id) return;
    const rows = await dispatch(getCycleMatieres(cycleEntity.id)).unwrap();
    setMatieres(rows);
    setSelectedMatiereId(current => (current && rows.some(matiere => matiere.id === current) ? current : rows[0]?.id));
  }, [cycleEntity?.id, dispatch]);

  useEffect(() => {
    fetchCycleMatieres();
  }, [fetchCycleMatieres]);

  useEffect(() => {
    const fetchEcoleMatieres = async () => {
      if (!cycleEntity?.ecoleId) return;
      const response = await dispatch(getEcoleMatieres(cycleEntity.ecoleId)).unwrap();
      setEcoleMatieres(response);
    };
    fetchEcoleMatieres();
  }, [cycleEntity?.ecoleId, dispatch]);

  const fetchSousMatieres = useCallback(
    async (matiereId = selectedMatiereId) => {
      const matiere = matieres.find(item => item.id === matiereId);
      if (!cycleEntity?.id || !matiere?.avecSousMatieres || !matiereId) {
        setSousMatieres([]);
        return;
      }
      const response = await dispatch(
        getCycleSousMatieres({ cycleMatiereId: matiere.cycleMatiereId, cycleId: cycleEntity.id, matiereId })
      ).unwrap();
      setSousMatieres(response);
    },
    [cycleEntity?.id, dispatch, matieres, selectedMatiereId]
  );

  useEffect(() => {
    fetchSousMatieres();
  }, [fetchSousMatieres]);

  const fetchSelectedMatiereCoefficients = useCallback(
    async (matiereId = selectedMatiereId) => {
      if (!cycleUsesCoefficients || !matiereId) {
        setCoefficientsByClasseId({});
        setCoefficientValues({});
        return;
      }

      setLoadingCoefficients(true);
      try {
        const coefficients = await dispatch(getMatiereCoefficients(matiereId)).unwrap();
        const nextByClasseId = coefficients.reduce<Record<string, ICycleCoefficient>>((acc, coefficient) => {
          if (coefficient.classeId) {
            acc[coefficient.classeId] = coefficient;
          }
          return acc;
        }, {});
        setCoefficientsByClasseId(nextByClasseId);
        setCoefficientValues(
          classes.reduce<Record<string, number | null>>((acc, classe) => {
            const rawValue = nextByClasseId[classe.id]?.coefficient;
            const value = Number(rawValue);
            acc[classe.id] = Number.isFinite(value) && value > 0 ? value : null;
            return acc;
          }, {})
        );
      } catch (error) {
        message.error('Impossible de charger les coefficients de cette matière.');
      } finally {
        setLoadingCoefficients(false);
      }
    },
    [classes, cycleUsesCoefficients, dispatch, selectedMatiereId]
  );

  useEffect(() => {
    fetchSelectedMatiereCoefficients();
  }, [fetchSelectedMatiereCoefficients]);

  const availableClasses = useMemo(() => {
    const currentIds = new Set(classes.map(c => c.id));
    return ecoleClasses.filter(c => !currentIds.has(c.id) && !c.cycleId);
  }, [ecoleClasses, classes]);

  const handleLinkClasse = async () => {
    if (selectedClasseIds.length === 0) return;
    setLinking(true);
    try {
      await dispatch(linkCycleClasses({ cycleId: cycleEntity.id, classeIds: selectedClasseIds })).unwrap();
      const response = await dispatch(getCycleClasses(props.match.params.id)).unwrap();
      setClasses(response);
      setSelectedClasseIds([]);
    } catch (error) {
      message.error('Cette classe est déjà associée à un autre cycle.');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkClasse = async (classeId: string) => {
    setLinking(true);
    try {
      await dispatch(unlinkCycleClasse(classeId)).unwrap();
      const response = await dispatch(getCycleClasses(props.match.params.id)).unwrap();
      setClasses(response);
    } finally {
      setLinking(false);
    }
  };

  const handleCreateSousMatiere = async () => {
    const nom = newSousMatiereName.trim();
    if (!nom || !cycleEntity?.id || !selectedMatiereId) return;
    const selected = matieres.find(matiere => matiere.id === selectedMatiereId);
    if (!selected?.cycleMatiereId) {
      message.warning("Enregistrez d'abord la matière dans le cycle.");
      return;
    }
    setSavingSousMatiere(true);
    try {
      await dispatch(createCycleSousMatiere({ nom, cycleMatiereId: selected.cycleMatiereId })).unwrap();
      setNewSousMatiereName('');
      await fetchSousMatieres();
    } catch (error) {
      message.error("Impossible d'enregistrer cette sous-matière.");
    } finally {
      setSavingSousMatiere(false);
    }
  };

  const handleLinkExistingMatiere = async () => {
    if (!cycleEntity?.id || !selectedExistingMatiereId) return;
    setSavingMatiere(true);
    try {
      await dispatch(
        linkExistingCycleMatiere({
          cycleId: cycleEntity.id,
          matiereId: selectedExistingMatiereId,
          avecSousMatieres: existingMatiereAvecSousMatieres,
        })
      ).unwrap();
      setSelectedMatiereId(selectedExistingMatiereId);
      setSelectedExistingMatiereId(undefined);
      setExistingMatiereAvecSousMatieres(false);
      await fetchCycleMatieres();
    } catch (error) {
      message.error("Impossible d'ajouter cette matière au cycle.");
    } finally {
      setSavingMatiere(false);
    }
  };

  const handleCreateMatiere = async () => {
    const nom = newMatiereName.trim();
    if (!cycleEntity?.id || !nom) return;
    setSavingMatiere(true);
    try {
      const result = await dispatch(
        createCycleMatiere({
          cycleId: cycleEntity.id,
          nom,
          ecoleId: cycleEntity.ecoleId,
          avecSousMatieres: matiereAvecSousMatieres,
        })
      ).unwrap();
      setNewMatiereName('');
      setMatiereAvecSousMatieres(false);
      setMatiereCreateModalOpen(false);
      setSelectedMatiereId(result?.id);
      await fetchCycleMatieres();
      if (cycleEntity?.ecoleId) {
        const response = await dispatch(getEcoleMatieres(cycleEntity.ecoleId)).unwrap();
        setEcoleMatieres(response);
      }
    } catch (error) {
      message.error('Impossible de créer cette matière.');
    } finally {
      setSavingMatiere(false);
    }
  };

  const handleUnlinkMatiere = async (matiereId: string) => {
    if (!cycleEntity?.id) return;
    setSavingMatiere(true);
    try {
      await dispatch(unlinkCycleMatiere({ cycleId: cycleEntity.id, matiereId })).unwrap();
      await fetchCycleMatieres();
    } catch (error) {
      message.error('Cette matière est déjà utilisée dans ce cycle.');
    } finally {
      setSavingMatiere(false);
    }
  };

  const handleUpdateSousMatiere = async (record: ICycleSousMatiere) => {
    const nom = editingSousMatiereName.trim();
    if (!nom) return;
    setSavingSousMatiere(true);
    try {
      await dispatch(updateCycleSousMatiere({ ...record, nom })).unwrap();
      setEditingSousMatiereId(null);
      setEditingSousMatiereName('');
      await fetchSousMatieres();
    } catch (error) {
      message.error('Impossible de modifier cette sous-matière.');
    } finally {
      setSavingSousMatiere(false);
    }
  };

  const handleDeleteSousMatiere = async (record: ICycleSousMatiere) => {
    setSavingSousMatiere(true);
    try {
      await dispatch(deleteCycleSousMatiere(record.id)).unwrap();
      await fetchSousMatieres();
    } catch (error) {
      message.error('Impossible de supprimer cette sous-matière.');
    } finally {
      setSavingSousMatiere(false);
    }
  };

  const handleCoefficientChange = (classeId: string, value: number | null) => {
    setCoefficientValues(current => ({ ...current, [classeId]: value }));
  };

  const handleSaveCoefficients = async () => {
    if (!cycleUsesCoefficients || !selectedMatiereId) return;

    setSavingCoefficients(true);
    try {
      await dispatch(saveCycleCoefficients({ matiereId: selectedMatiereId, classes, coefficientValues, coefficientsByClasseId })).unwrap();
      message.success('Coefficients enregistrés.');
      await fetchSelectedMatiereCoefficients();
    } catch (error) {
      message.error("Impossible d'enregistrer les coefficients.");
    } finally {
      setSavingCoefficients(false);
    }
  };

  const goBack = () => props.history.goBack();
  const classesTable = (
    <Table
      dataSource={classes}
      rowKey="id"
      pagination={false}
      columns={[
        {
          title: <Translate contentKey="korischoolApp.cycle.classes">Classes</Translate>,
          dataIndex: 'nom',
          key: 'nom',
        },
        {
          title: '',
          key: 'actions',
          render: (_, record) => (
            <Button size="sm" color="danger" onClick={() => setClasseToUnlink(record)} disabled={linking}>
              <Translate contentKey="korischoolApp.cycle.unlinkClasse">Retirer</Translate>
            </Button>
          ),
        },
      ]}
      locale={{ emptyText: '-' }}
      size="small"
      style={{ marginBottom: 16 }}
    />
  );

  const title = (
    <Space direction="vertical" size={0}>
      <Typography.Text type="secondary">
        <Translate contentKey="korischoolApp.cycle.detail.title">Cycle</Translate>
      </Typography.Text>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {cycleEntity.nom || '-'}
      </Typography.Title>
      <Typography.Text type="secondary">{cycleEntity.scoringMode ? scoringModeLabels[cycleEntity.scoringMode] : '-'}</Typography.Text>
    </Space>
  );

  const availableMatieres = useMemo(() => {
    const currentIds = new Set(matieres.map(matiere => matiere.id));
    return ecoleMatieres.filter(matiere => !currentIds.has(matiere.id));
  }, [ecoleMatieres, matieres]);

  const selectedMatiere = matieres.find(matiere => matiere.id === selectedMatiereId);
  const selectedMatiereAvecSousMatieres = Boolean(selectedMatiere?.avecSousMatieres);

  return (
    <Row className="justify-content-center">
      <Col md="10">
        <Card title={title} extra={null} bodyStyle={{ paddingTop: 12 }}>
          <Tabs defaultActiveKey="classes" className="cycle-detail-tabs">
            <Tabs.TabPane tab="Classes" key="classes">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <section>
                  <Typography.Text type="secondary">
                    <Translate contentKey="korischoolApp.cycle.classes">Classes</Translate>
                  </Typography.Text>
                  {classesTable}
                </section>

                <section>
                  <FormGroup>
                    <Label for="cycle-add-classe">
                      <Translate contentKey="korischoolApp.cycle.addClasse">Ajouter classe</Translate>
                    </Label>
                    <Select
                      id="cycle-add-classe"
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder="Ajouter des classes"
                      value={selectedClasseIds}
                      onChange={values => setSelectedClasseIds(values)}
                      showSearch
                      optionFilterProp="children"
                    >
                      {availableClasses.map(c => (
                        <Select.Option value={c.id} key={c.id}>
                          {c.nom}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormGroup>
                  <Space>
                    <Button onClick={goBack} color="info" data-cy="entityDetailsBackButton">
                      <FontAwesomeIcon icon="arrow-left" />{' '}
                      <span className="d-none d-md-inline">
                        <Translate contentKey="entity.action.back">Back</Translate>
                      </span>
                    </Button>
                    <Button color="primary" onClick={handleLinkClasse} disabled={selectedClasseIds.length === 0 || linking}>
                      <Translate contentKey="korischoolApp.cycle.linkClasse">Ajouter</Translate>
                    </Button>
                  </Space>
                </section>
              </Space>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Matières" key="matieres">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <section>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                    <Space direction="vertical" size={0}>
                      <Typography.Text type="secondary">Configuration pédagogique</Typography.Text>
                      <Typography.Text strong>
                        {matieres.length} matière{matieres.length > 1 ? 's' : ''} configurée{matieres.length > 1 ? 's' : ''}
                      </Typography.Text>
                    </Space>
                    <Button style={orangeButtonStyle} onClick={() => setMatiereCreateModalOpen(true)}>
                      Créer une matière
                    </Button>
                  </div>

                  <Row>
                    <Col md="5">
                      <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, minHeight: 320, padding: 8 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                          <Space direction="vertical" size={6} style={{ flex: 1 }}>
                            <Select
                              style={{ width: '100%' }}
                              placeholder="Lier une matière existante"
                              value={selectedExistingMatiereId}
                              onChange={value => setSelectedExistingMatiereId(value)}
                              showSearch
                              allowClear
                              optionFilterProp="children"
                            >
                              {availableMatieres.map(matiere => (
                                <Select.Option value={matiere.id} key={matiere.id}>
                                  {matiere.nom}
                                </Select.Option>
                              ))}
                            </Select>
                            <Checkbox
                              checked={existingMatiereAvecSousMatieres}
                              onChange={event => setExistingMatiereAvecSousMatieres(event.target.checked)}
                            >
                              Cette matière a des sous-matières
                            </Checkbox>
                          </Space>
                          <Button
                            color="secondary"
                            onClick={handleLinkExistingMatiere}
                            disabled={!selectedExistingMatiereId || savingMatiere}
                          >
                            Lier
                          </Button>
                        </div>

                        {matieres.length === 0 ? (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune matière dans ce cycle" />
                        ) : (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            {matieres.map(matiere => {
                              const selected = matiere.id === selectedMatiereId;
                              return (
                                <button
                                  type="button"
                                  key={matiere.id}
                                  onClick={() => setSelectedMatiereId(matiere.id)}
                                  style={{
                                    width: '100%',
                                    minHeight: 44,
                                    border: selected ? `1px solid ${KORI_ORANGE}` : '1px solid #f0f0f0',
                                    borderRadius: 6,
                                    background: selected ? KORI_ORANGE_LIGHT : '#fff',
                                    color: '#262626',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 10px',
                                    textAlign: 'left',
                                  }}
                                >
                                  <span>{matiere.nom}</span>
                                  {selected && <Tag color="orange">sélectionnée</Tag>}
                                </button>
                              );
                            })}
                          </Space>
                        )}
                      </div>
                    </Col>

                    <Col md="7">
                      <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, minHeight: 320, padding: 12 }}>
                        {selectedMatiere ? (
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                              <Space direction="vertical" size={0}>
                                <Typography.Text type="secondary">Matière sélectionnée</Typography.Text>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                  {selectedMatiere.nom}
                                </Typography.Title>
                              </Space>
                              <Button
                                color="link"
                                title="Retirer du cycle"
                                aria-label="Retirer du cycle"
                                onClick={() => handleUnlinkMatiere(selectedMatiere.id)}
                                disabled={savingMatiere}
                                style={{ color: '#dc3545', borderColor: '#dc3545', padding: '4px 8px' }}
                              >
                                <DeleteOutlined />
                              </Button>
                            </div>

                            {selectedMatiereAvecSousMatieres && (
                              <>
                                <Divider style={{ margin: '4px 0' }} />
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Typography.Text type="secondary">Sous-matières de {selectedMatiere.nom}</Typography.Text>
                                  <Input.Group compact style={{ display: 'flex', width: '100%' }}>
                                    <Input
                                      placeholder="Nouvelle sous-matière"
                                      value={newSousMatiereName}
                                      onChange={event => setNewSousMatiereName(event.target.value)}
                                      onPressEnter={handleCreateSousMatiere}
                                      disabled={savingSousMatiere}
                                      style={{ flex: 1 }}
                                    />
                                    <Button
                                      style={orangeButtonStyle}
                                      onClick={handleCreateSousMatiere}
                                      disabled={!newSousMatiereName.trim() || savingSousMatiere}
                                    >
                                      Ajouter
                                    </Button>
                                  </Input.Group>
                                  <Table
                                    dataSource={sousMatieres}
                                    rowKey="id"
                                    pagination={false}
                                    loading={savingSousMatiere}
                                    size="small"
                                    columns={[
                                      {
                                        title: 'Nom',
                                        dataIndex: 'nom',
                                        key: 'nom',
                                        render: (_, record) =>
                                          editingSousMatiereId === record.id ? (
                                            <Input
                                              value={editingSousMatiereName}
                                              onChange={event => setEditingSousMatiereName(event.target.value)}
                                              onPressEnter={() => handleUpdateSousMatiere(record)}
                                            />
                                          ) : (
                                            record.nom
                                          ),
                                      },
                                      {
                                        title: '',
                                        key: 'actions',
                                        render: (_, record) =>
                                          editingSousMatiereId === record.id ? (
                                            <Space>
                                              <Button size="sm" color="primary" onClick={() => handleUpdateSousMatiere(record)}>
                                                Enregistrer
                                              </Button>
                                              <Button size="sm" color="secondary" onClick={() => setEditingSousMatiereId(null)}>
                                                Annuler
                                              </Button>
                                            </Space>
                                          ) : (
                                            <Space>
                                              <Button
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                  setEditingSousMatiereId(record.id);
                                                  setEditingSousMatiereName(record.nom);
                                                }}
                                              >
                                                Modifier
                                              </Button>
                                              <Button size="sm" color="danger" onClick={() => handleDeleteSousMatiere(record)}>
                                                Supprimer
                                              </Button>
                                            </Space>
                                          ),
                                      },
                                    ]}
                                    locale={{ emptyText: 'Aucune sous-matière' }}
                                  />
                                </Space>
                              </>
                            )}

                            {!selectedMatiereAvecSousMatieres && (
                              <Typography.Text type="secondary">
                                Cette matière n’utilise pas de sous-matières dans ce cycle.
                              </Typography.Text>
                            )}

                            {cycleUsesCoefficients && (
                              <>
                                <Divider style={{ margin: '4px 0' }} />
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Typography.Text type="secondary">
                                    {selectedMatiere.coefficientSource === 'SOUS_MATIERES'
                                      ? 'Coefficients généraux conservés, non utilisés : les coefficients des sous-matières sont actifs.'
                                      : 'Coefficients généraux par classe'}
                                  </Typography.Text>
                                  <Link to={`/configuration-pedagogie/${props.match.params.ecoleid}/wizard/${cycleEntity.id}`}>
                                    Configurer la stratégie et les sous-coefficients dans le wizard
                                  </Link>
                                  <Table
                                    dataSource={classes}
                                    rowKey="id"
                                    pagination={false}
                                    loading={loadingCoefficients}
                                    size="small"
                                    columns={[
                                      {
                                        title: 'Classe',
                                        dataIndex: 'nom',
                                        key: 'nom',
                                      },
                                      {
                                        title: 'Coefficient',
                                        key: 'coefficient',
                                        width: 180,
                                        render: (_, record) => (
                                          <InputNumber
                                            min={1}
                                            precision={0}
                                            value={coefficientValues[record.id] ?? undefined}
                                            placeholder="Ex: 4"
                                            onChange={value => handleCoefficientChange(record.id, value)}
                                            style={{ width: '100%' }}
                                          />
                                        ),
                                      },
                                    ]}
                                    locale={{ emptyText: 'Aucune classe dans ce cycle' }}
                                  />
                                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                      style={orangeButtonStyle}
                                      onClick={handleSaveCoefficients}
                                      disabled={classes.length === 0 || savingCoefficients}
                                    >
                                      Enregistrer les coefficients
                                    </Button>
                                  </div>
                                </Space>
                              </>
                            )}
                          </Space>
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionner ou créer une matière" />
                        )}
                      </div>
                    </Col>
                  </Row>
                </section>
              </Space>
            </Tabs.TabPane>
          </Tabs>
        </Card>

        <Modal isOpen={matiereCreateModalOpen} toggle={() => setMatiereCreateModalOpen(false)}>
          <ModalHeader toggle={() => setMatiereCreateModalOpen(false)}>Créer une matière pour ce cycle</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="cycle-new-matiere">Nom de la matière</Label>
              <Input
                id="cycle-new-matiere"
                placeholder="Ex: Mathématiques"
                value={newMatiereName}
                onChange={event => setNewMatiereName(event.target.value)}
                onPressEnter={handleCreateMatiere}
                disabled={savingMatiere}
              />
            </FormGroup>
            <FormGroup>
              <Checkbox checked={matiereAvecSousMatieres} onChange={event => setMatiereAvecSousMatieres(event.target.checked)}>
                Cette matière a des sous-matières
              </Checkbox>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button style={orangeButtonStyle} onClick={handleCreateMatiere} disabled={!newMatiereName.trim() || savingMatiere}>
              Créer
            </Button>
            <Button color="secondary" onClick={() => setMatiereCreateModalOpen(false)} disabled={savingMatiere}>
              Annuler
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={!!classeToUnlink} toggle={() => setClasseToUnlink(null)}>
          <ModalHeader toggle={() => setClasseToUnlink(null)}>
            <Translate contentKey="entity.delete.title">Confirm delete operation</Translate>
          </ModalHeader>
          <ModalBody>
            <Translate contentKey="korischoolApp.cycle.unlinkConfirm" /> <strong>{classeToUnlink?.nom}</strong> ?
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                const id = classeToUnlink?.id;
                setClasseToUnlink(null);
                if (id) handleUnlinkClasse(id);
              }}
              disabled={linking}
            >
              <Translate contentKey="entity.action.delete">Delete</Translate>
            </Button>
            <Button color="secondary" onClick={() => setClasseToUnlink(null)} disabled={linking}>
              <Translate contentKey="entity.action.cancel">Cancel</Translate>
            </Button>
          </ModalFooter>
        </Modal>
      </Col>
    </Row>
  );
};

export default CycleDetail;
